import 'moment/locale/th';
import React, { useState, useEffect } from 'react';
import { formatComma, tableWidth, getTheme } from 'helpers/helpers';
import callApi from 'helpers/callapi';
import moment from 'moment';
import _, { get, find, orderBy, uniqBy, filter } from 'lodash';
import { Link } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

// Components
import CONSTANTS from 'constants/constants';
import { Button, Table, Pagination, Modal, Row, Col, Select, message, Input, Upload, Radio, Spin, Tooltip, Tag } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';
import { filterLabelAndValue } from 'helpers/helpers';

// constants
import ROUNDS from 'constants/rounds';
import TCAS_STATUSES from 'constants/tcas_statuses';
import DownloadModal from './DownloadModal';
import { DiffValue } from 'helpers/site-history';

// import AdminDetailModal from './AdminModal';

const ORDER_STATUS = CONSTANTS.orderStatus;
const ORDER_LABEL = CONSTANTS.orderLabels;
const BANK = CONSTANTS.banks;

const Option = Select.Option;

moment.locale('th');

// ปีที่แล้วที่ snapshot ไว้ใน examsitehistories (ดู tasks/exams/snapshot-site-history.js ฝั่ง API)
const HISTORY_YEAR = '69';

// ห้อง/ที่นั่งรวมทุกประเภท (ปกติ + พิเศษ + CBT) ให้ตรงกับคอลัมน์ "ห้อง" และ "ที่นั่งสอบ" ของตารางปีนี้
const totalStat = (stat) => ({
  rooms: (get(stat, 'rooms') || 0) + (get(stat, 'special_rooms') || 0) + (get(stat, 'cbt_rooms') || 0),
  seats: (get(stat, 'seats') || 0) + (get(stat, 'special_seats') || 0) + (get(stat, 'cbt_seats') || 0),
});

const num = (value) => (value ? formatComma(value) : '-');

// ผลต่าง = ปีนี้ − ปี 69 ใช้ป้ายลูกศรชุดเดียวกับหน้าอื่น (helpers/site-history)
const renderDiff = (current, previous) => <DiffValue current={current} previous={previous} />;

// จับคู่สนามข้ามปีด้วยรหัสโรงเรียน (site_school_code) เพราะ site_code อาจเปลี่ยนเมื่อลบสนามแล้วเพิ่มใหม่
const siteKey = (site) => site.site_school_code || site.site_code;

// รวมสนามที่รหัสโรงเรียนเดียวกัน (สนาม PBT + สนาม CBT ของที่เดียวกัน) เป็นรายการเดียว
const aggregateBySchool = (list) => {
  const out = {};
  list.forEach((site) => {
    const key = siteKey(site);
    const stat = totalStat(site.stats);
    if (!out[key]) out[key] = { rooms: 0, seats: 0, cbtRooms: 0, cbtSeats: 0, site, hasCbt: false, cbtOnly: true };
    out[key].rooms += stat.rooms;
    out[key].seats += stat.seats;
    if (site.exam_type === 'cbt') {
      out[key].hasCbt = true;
      out[key].cbtRooms += stat.rooms;
      out[key].cbtSeats += stat.seats;
    } else {
      out[key].site = site; // ใช้ชื่อ/รหัสของสนาม PBT เป็นหลัก
      out[key].cbtOnly = false;
    }
  });
  return out;
};

const buildCompareRows = (currentSites, historySites) => {
  const currentByKey = aggregateBySchool(currentSites);
  const historyByKey = aggregateBySchool(historySites);

  const toRow = (key, current, previous) => {
    const site = (current || previous).site;
    return {
      key,
      school_name: site.school_name,
      site_school_code: site.site_school_code,
      province: site.province,
      amphur: site.amphur,
      cbtOnly: Boolean(previous && previous.cbtOnly && (!current || current.cbtOnly)),
      previousCbt: previous && previous.hasCbt && !previous.cbtOnly ? { rooms: previous.cbtRooms, seats: previous.cbtSeats } : null,
      current: current ? { rooms: current.rooms, seats: current.seats } : null,
      previous: previous ? { rooms: previous.rooms, seats: previous.seats } : null,
      status: !previous ? 'new' : !current ? 'removed' : 'same',
    };
  };

  const rows = [];
  const seen = {};
  // เรียงตามลำดับสนามปีนี้ก่อน แล้วต่อด้วยสนามที่มีแต่ปี 69
  currentSites.forEach((site) => {
    const key = siteKey(site);
    if (seen[key]) return;
    seen[key] = true;
    rows.push(toRow(key, currentByKey[key], historyByKey[key]));
  });
  _.forEach(historyByKey, (previous, key) => {
    if (!seen[key]) rows.push(toRow(key, null, previous));
  });
  return rows;
};

const sumRows = (rows) =>
  rows.reduce(
    (acc, row) => ({
      current_sites: acc.current_sites + (row.current ? 1 : 0),
      previous_sites: acc.previous_sites + (row.previous ? 1 : 0),
      current: {
        rooms: acc.current.rooms + get(row, 'current.rooms', 0),
        seats: acc.current.seats + get(row, 'current.seats', 0),
      },
      previous: {
        rooms: acc.previous.rooms + get(row, 'previous.rooms', 0),
        seats: acc.previous.seats + get(row, 'previous.seats', 0),
      },
    }),
    { current_sites: 0, previous_sites: 0, current: { rooms: 0, seats: 0 }, previous: { rooms: 0, seats: 0 } },
  );

const STATUS_TAGS = {
  new: <Tag color="green">ใหม่ปีนี้</Tag>,
  removed: <Tag color="red">ไม่มีปีนี้</Tag>,
  same: null,
};

export default () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const me = useSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const adminConfig = useSelector((state) => state.configs || {});

  const [sites, setSites] = useState([]);
  const [historySites, setHistorySites] = useState([]);
  const [view, setView] = useState('current'); // current | compare | province
  const [modalVisible, setModalVisible] = useState(false);

  const getSchools = async () => {
    const _sites = await callApi({ url: '/exam-centers/me/sites' });
    setSites(_sites.data);
  };
  const getHistory = async () => {
    try {
      const result = await callApi({ url: `/exam-centers/me/history/${HISTORY_YEAR}/sites` });
      setHistorySites(result.data || []);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    getSchools();
    getHistory();
  }, []);

  const compareRows = buildCompareRows(sites, historySites);
  const compareTotal = sumRows(compareRows);
  const provinceRows = _.sortBy(
    _.map(_.groupBy(compareRows, 'province'), (rows, province) => ({ province, ...sumRows(rows) })),
    (row) => -Math.max(row.current.seats, row.previous.seats),
  );

  const renderCompareTable = () => (
    <table className="t-table t-table-striped">
      <thead className="th-orange">
        <tr>
          <th rowSpan="2">ลำดับ</th>
          <th rowSpan="2">ชื่อสนามสอบ</th>
          <th rowSpan="2">รหัส</th>
          <th rowSpan="2">จังหวัด</th>
          <th rowSpan="2">อำเภอ</th>
          <th colSpan="3" className="text-center t-group-start">
            ห้อง
          </th>
          <th colSpan="3" className="text-center t-group-start">
            ที่นั่งสอบ
          </th>
        </tr>
        <tr>
          <th className="text-right t-group-start">ปี {HISTORY_YEAR}</th>
          <th className="text-right">ปีนี้</th>
          <th className="text-right">ผลต่าง</th>
          <th className="text-right t-group-start">ปี {HISTORY_YEAR}</th>
          <th className="text-right">ปีนี้</th>
          <th className="text-right">ผลต่าง</th>
        </tr>
      </thead>
      <tbody>
        {compareRows.map((row, index) => (
          <tr key={row.key} style={row.status === 'removed' ? { color: '#979797' } : {}}>
            <td>{index + 1}</td>
            <td>
              {row.school_name} {row.cbtOnly && <Tag color="blue">CBT</Tag>}
              {STATUS_TAGS[row.status]}
              {row.previousCbt && (
                <>
                  <br />
                  <small style={{ color: '#1a477f' }}>
                    ปี {HISTORY_YEAR} CBT {row.previousCbt.rooms} ห้อง : {formatComma(row.previousCbt.seats)} ที่นั่ง
                  </small>
                </>
              )}
            </td>
            <td>{row.site_school_code}</td>
            <td>{row.province}</td>
            <td>{row.amphur}</td>
            <td className="_nowrap text-right t-group-start">{num(get(row, 'previous.rooms'))}</td>
            <td className="_nowrap text-right">{num(get(row, 'current.rooms'))}</td>
            <td className="_nowrap text-right">{renderDiff(get(row, 'current.rooms'), get(row, 'previous.rooms'))}</td>
            <td className="_nowrap text-right t-group-start">{num(get(row, 'previous.seats'))}</td>
            <td className="_nowrap text-right">
              <b>{num(get(row, 'current.seats'))}</b>
            </td>
            <td className="_nowrap text-right">{renderDiff(get(row, 'current.seats'), get(row, 'previous.seats'))}</td>
          </tr>
        ))}
        <tr style={{ fontWeight: 'bold', background: '#fff6e5' }}>
          <td colSpan="5">
            รวมทั้งศูนย์สอบ (ปี {HISTORY_YEAR} {compareTotal.previous_sites} แห่ง / ปีนี้ {compareTotal.current_sites} แห่ง)
          </td>
          <td className="_nowrap text-right t-group-start">{num(compareTotal.previous.rooms)}</td>
          <td className="_nowrap text-right">{num(compareTotal.current.rooms)}</td>
          <td className="_nowrap text-right">{renderDiff(compareTotal.current.rooms, compareTotal.previous.rooms)}</td>
          <td className="_nowrap text-right t-group-start">{num(compareTotal.previous.seats)}</td>
          <td className="_nowrap text-right">{num(compareTotal.current.seats)}</td>
          <td className="_nowrap text-right">{renderDiff(compareTotal.current.seats, compareTotal.previous.seats)}</td>
        </tr>
      </tbody>
    </table>
  );

  const renderProvinceTable = () => (
    <table className="t-table t-table-striped">
      <thead className="th-orange">
        <tr>
          <th rowSpan="2">จังหวัด</th>
          <th colSpan="2" className="text-center t-group-start">
            สนามสอบ
          </th>
          <th colSpan="3" className="text-center t-group-start">
            ห้อง
          </th>
          <th colSpan="3" className="text-center t-group-start">
            ที่นั่งสอบ
          </th>
        </tr>
        <tr>
          <th className="text-right t-group-start">ปี {HISTORY_YEAR}</th>
          <th className="text-right">ปีนี้</th>
          <th className="text-right t-group-start">ปี {HISTORY_YEAR}</th>
          <th className="text-right">ปีนี้</th>
          <th className="text-right">ผลต่าง</th>
          <th className="text-right t-group-start">ปี {HISTORY_YEAR}</th>
          <th className="text-right">ปีนี้</th>
          <th className="text-right">ผลต่าง</th>
        </tr>
      </thead>
      <tbody>
        {provinceRows.map((row) => (
          <tr key={row.province}>
            <td>{row.province}</td>
            <td className="text-right t-group-start">{num(row.previous_sites)}</td>
            <td className="text-right">{num(row.current_sites)}</td>
            <td className="_nowrap text-right t-group-start">{num(row.previous.rooms)}</td>
            <td className="_nowrap text-right">{num(row.current.rooms)}</td>
            <td className="_nowrap text-right">{renderDiff(row.current.rooms, row.previous.rooms)}</td>
            <td className="_nowrap text-right t-group-start">{num(row.previous.seats)}</td>
            <td className="_nowrap text-right">
              <b>{num(row.current.seats)}</b>
            </td>
            <td className="_nowrap text-right">{renderDiff(row.current.seats, row.previous.seats)}</td>
          </tr>
        ))}
        <tr style={{ fontWeight: 'bold', background: '#fff6e5' }}>
          <td>รวมทั้งศูนย์สอบ</td>
          <td className="text-right t-group-start">{num(compareTotal.previous_sites)}</td>
          <td className="text-right">{num(compareTotal.current_sites)}</td>
          <td className="_nowrap text-right t-group-start">{num(compareTotal.previous.rooms)}</td>
          <td className="_nowrap text-right">{num(compareTotal.current.rooms)}</td>
          <td className="_nowrap text-right">{renderDiff(compareTotal.current.rooms, compareTotal.previous.rooms)}</td>
          <td className="_nowrap text-right t-group-start">{num(compareTotal.previous.seats)}</td>
          <td className="_nowrap text-right">{num(compareTotal.current.seats)}</td>
          <td className="_nowrap text-right">{renderDiff(compareTotal.current.seats, compareTotal.previous.seats)}</td>
        </tr>
      </tbody>
    </table>
  );

  // {['1', '2', '4'].map((roundNumber) => {
  //   const round = find(ROUNDS, { value: `${roundNumber}_2565` });

  //   if (adminConfig.current_round !== roundNumber) {
  //     return <></>;
  //   }

  // }

  return (
    <div>
      <Spin spinning={loading} delay={0}>
        <div>
          <div className="t-main">
            <div className="flex justify-between">
              <h1>ข้อมูลศูนย์สอบ</h1>
              <div className="action items-center">
                {/* <a href="" className="btn-ghost t-bg-while">
                  นำเข้าข้อมูล CSV
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9.96663 1.6665C5.36413 1.6665 1.6333 5.39734 1.6333 9.99984C1.6333 14.6023 5.36413 18.3332 9.96663 18.3332C14.5691 18.3332 18.3 14.6023 18.3 9.99984C18.3 5.39734 14.5691 1.6665 9.96663 1.6665ZM8.12419 6.73079C8.22377 6.73079 8.32384 6.75447 8.41634 6.80322L13.4383 9.44727C13.6446 9.5556 13.7724 9.76773 13.772 10.0007C13.772 10.2336 13.6446 10.4449 13.4383 10.5532L8.41634 13.1965C8.32426 13.2448 8.22377 13.2689 8.12419 13.2689C8.01252 13.2689 7.9015 13.2385 7.80192 13.1785C7.61275 13.0644 7.5 12.8639 7.5 12.6431V7.35661C7.5 7.13577 7.61275 6.93529 7.80192 6.82113C7.9015 6.76071 8.01294 6.73079 8.12419 6.73079Z"
                      fill="currentColor"
                    />
                  </svg>
                </a> */}
                <a
                  className="btn-ghost t-bg-while"
                  onClick={() => {
                    setModalVisible(true);
                  }}
                >
                  ดาวน์โหลด CSV
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9.96663 1.6665C5.36413 1.6665 1.6333 5.39734 1.6333 9.99984C1.6333 14.6023 5.36413 18.3332 9.96663 18.3332C14.5691 18.3332 18.3 14.6023 18.3 9.99984C18.3 5.39734 14.5691 1.6665 9.96663 1.6665ZM8.12419 6.73079C8.22377 6.73079 8.32384 6.75447 8.41634 6.80322L13.4383 9.44727C13.6446 9.5556 13.7724 9.76773 13.772 10.0007C13.772 10.2336 13.6446 10.4449 13.4383 10.5532L8.41634 13.1965C8.32426 13.2448 8.22377 13.2689 8.12419 13.2689C8.01252 13.2689 7.9015 13.2385 7.80192 13.1785C7.61275 13.0644 7.5 12.8639 7.5 12.6431V7.35661C7.5 7.13577 7.61275 6.93529 7.80192 6.82113C7.9015 6.76071 8.01294 6.73079 8.12419 6.73079Z"
                      fill="currentColor"
                    />
                  </svg>
                </a>
              </div>
            </div>
            {me.center && (
              <div className="t-form border-blue">
                รหัสศูนย์สอบ : {me.center.center_code}
                <br />
                ศูนย์สอบ : {me.center.center_name}
                <br />
              </div>
            )}

            <h2>
              <span>
                <svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M18.75 8.50001H16.5V4.00001C16.5 3.75251 16.378 3.52101 16.1735 3.38101L11.4235 0.131006C11.168 -0.0439941 10.8315 -0.0439941 10.5765 0.131006L5.8265 3.38101C5.622 3.52101 5.5 3.75251 5.5 4.00001V8.50001H3.25C1.7335 8.50001 0.5 9.73401 0.5 11.25V20.25C0.5 20.664 0.8355 21 1.25 21H20.75C21.1645 21 21.5 20.664 21.5 20.25V11.25C21.5 9.73401 20.2665 8.50001 18.75 8.50001ZM5.25 17.5C5.25 17.776 5.026 18 4.75 18H4.25C3.974 18 3.75 17.776 3.75 17.5V15C3.75 14.724 3.974 14.5 4.25 14.5H4.75C5.026 14.5 5.25 14.724 5.25 15V17.5ZM5.25 12.5C5.25 12.776 5.026 13 4.75 13H4.25C3.974 13 3.75 12.776 3.75 12.5V12C3.75 11.724 3.974 11.5 4.25 11.5H4.75C5.026 11.5 5.25 11.724 5.25 12V12.5ZM8.5 17.5C8.5 17.776 8.276 18 8 18H7.5C7.224 18 7 17.776 7 17.5V15C7 14.724 7.224 14.5 7.5 14.5H8C8.276 14.5 8.5 14.724 8.5 15V17.5ZM8.5 12.5C8.5 12.776 8.276 13 8 13H7.5C7.224 13 7 12.776 7 12.5V12C7 11.724 7.224 11.5 7.5 11.5H8C8.276 11.5 8.5 11.724 8.5 12V12.5ZM11.75 17.5C11.75 17.776 11.526 18 11.25 18H10.75C10.474 18 10.25 17.776 10.25 17.5V15C10.25 14.724 10.474 14.5 10.75 14.5H11.25C11.526 14.5 11.75 14.724 11.75 15V17.5ZM11.75 12.5C11.75 12.776 11.526 13 11.25 13H10.75C10.474 13 10.25 12.776 10.25 12.5V12C10.25 11.724 10.474 11.5 10.75 11.5H11.25C11.526 11.5 11.75 11.724 11.75 12V12.5ZM11 9.00001C9.8955 9.00001 9 8.10451 9 7.00001C9 5.89551 9.8955 5.00001 11 5.00001C12.1045 5.00001 13 5.89551 13 7.00001C13 8.10451 12.1045 9.00001 11 9.00001ZM15 17.5C15 17.776 14.776 18 14.5 18H14C13.724 18 13.5 17.776 13.5 17.5V15C13.5 14.724 13.724 14.5 14 14.5H14.5C14.776 14.5 15 14.724 15 15V17.5ZM15 12.5C15 12.776 14.776 13 14.5 13H14C13.724 13 13.5 12.776 13.5 12.5V12C13.5 11.724 13.724 11.5 14 11.5H14.5C14.776 11.5 15 11.724 15 12V12.5ZM18.25 17.5C18.25 17.776 18.026 18 17.75 18H17.25C16.974 18 16.75 17.776 16.75 17.5V15C16.75 14.724 16.974 14.5 17.25 14.5H17.75C18.026 14.5 18.25 14.724 18.25 15V17.5ZM18.25 12.5C18.25 12.776 18.026 13 17.75 13H17.25C16.974 13 16.75 12.776 16.75 12.5V12C16.75 11.724 16.974 11.5 17.25 11.5H17.75C18.026 11.5 18.25 11.724 18.25 12V12.5Z"
                    fill="#FF8A00"
                  />
                </svg>
              </span>{' '}
              {view === 'province' ? `ครอบคลุม ${provinceRows.length} จังหวัด` : `มีสนามสอบ ${sites.length} แห่ง`}
              {view !== 'current' && (
                <small style={{ fontWeight: 'normal', color: '#828897', marginLeft: 12 }}>
                  เทียบกับปี {HISTORY_YEAR} (ห้อง/ที่นั่งรวมห้องพิเศษและ CBT · จับคู่สนามด้วยรหัสโรงเรียน)
                </small>
              )}
            </h2>
            <div className="flex justify-between" style={{ marginBottom: 12 }}>
              <div></div>
              <Radio.Group onChange={(e) => setView(e.target.value)} value={view} buttonStyle="solid">
                <Radio.Button value="current">ปีนี้</Radio.Button>
                <Radio.Button value="compare">เทียบปี {HISTORY_YEAR}</Radio.Button>
                <Radio.Button value="province">รายจังหวัด</Radio.Button>
              </Radio.Group>
            </div>
            {view === 'compare' && renderCompareTable()}
            {view === 'province' && renderProvinceTable()}
            {view === 'current' && (
            <table className="t-table t-table-striped">
              <thead className="th-orange">
                <tr>
                  <th>ลำดับ</th>
                  <th>ชื่อสนามสอบ</th>
                  <th>รหัส</th>
                  <th>จังหวัด</th>
                  <th>อำเภอ</th>
                  <th className="text-right">ห้อง</th>
                  <th className="text-right">ที่นั่งสอบ</th>
                  <th className="text-center">
                    <Tooltip title="ห้องสอบปกติ กระดาษ">
                      <i className="i-paper"></i>
                    </Tooltip>
                  </th>
                  <th className="text-center">
                    <Tooltip title="ห้องสอบสำหรับผู้ต้องการความช่วยเหลือพิเศษ กระดาษ">
                      <i className="i-disabled"></i>
                    </Tooltip>
                  </th>
                  <th className="text-center">
                    <Tooltip title="ห้องสอบปกติ คอมพิวเตอร์">
                      <i className="i-computer"></i>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site, index) => {
                  const stat = get(site, `stats`);
                  return (
                    <tr>
                      <td>{index + 1}</td>
                      <td>{site.school_name}</td>
                      <td>{site.site_school_code}</td>
                      <td>{site.province}</td>
                      <td>{site.amphur}</td>

                      <td className="_nowrap text-right">
                        {get(stat, 'rooms') + (get(stat, 'special_rooms') || 0) + (get(stat, 'cbt_rooms') || 0)}
                      </td>
                      <td className="_nowrap text-right">
                        {get(stat, 'seats') + (get(stat, 'special_seats') || 0) + (get(stat, 'cbt_seats') || 0)}
                      </td>

                      <td className="_nowrap text-center">
                        {get(stat, 'rooms')} : {get(stat, 'seats')}
                      </td>
                      <td className="_nowrap text-center">
                        {get(stat, 'special_rooms')} : {get(stat, 'special_seats')}
                      </td>
                      <td className="_nowrap text-center">
                        {get(stat, 'cbt_rooms')} : {get(stat, 'cbt_seats')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
            <br />
          </div>
        </div>
      </Spin>
      <DownloadModal
        setIsVisible={setModalVisible}
        isVisible={modalVisible}
        centerCode={me.center_code}
      ></DownloadModal>
    </div>
  );
};

const ContentBody = styled.div`
  background-color: white;
  border: 1px solid ${getTheme`colors.gray`};
  padding: 20px;
`;
